// src/features/brain/BrainFeatureDisabledState.tsx
// Phase 6.7.4 - Feature-disabled unlock state for Property Brain.
//
// Rendered inside BrainPanel when useBrainFeatureEnabled returns false.
// Replaces the chat UI with a styled upsell prompt directing the user
// to contact support to enable Brain for their organization.
//
// When billing infrastructure ships in Phase 8.4, the "Contact us" mailto
// link is replaced with an in-app upgrade flow.

import { useTranslation } from "react-i18next";
import { Lock, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const SUPPORT_EMAIL = "support@maplekey.app";
const LEARN_MORE_URL = "https://maplekey.app/ai";

export function BrainFeatureDisabledState() {
  const { t } = useTranslation();

  const mailtoHref =
    `mailto:${SUPPORT_EMAIL}` +
    `?subject=${encodeURIComponent(t("brain.disabled.contactSubject"))}` +
    `&body=${encodeURIComponent(t("brain.disabled.contactBody"))}`;

  return (
    <div className="flex flex-col h-full items-center justify-center px-6 py-8 text-center">
      <div className="rounded-full bg-primary/10 p-4 mb-4">
        <Lock className="h-6 w-6 text-primary" />
      </div>

      <h2 className="text-lg font-semibold mb-2">
        {t("brain.disabled.title")}
      </h2>

      <p className="text-sm text-muted-foreground mb-6 max-w-sm">
        {t("brain.disabled.description")}
      </p>

      <div className="flex flex-col items-center gap-3 w-full max-w-xs">
        <Button asChild className="w-full min-h-[44px]">
          <a href={mailtoHref}>{t("brain.disabled.contactButton")}</a>
        </Button>

        <Button asChild variant="ghost" size="sm" className="min-h-[44px]">
          <a
            href={LEARN_MORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            {t("brain.disabled.learnMore")}
            <ExternalLink className="h-3 w-3 ml-1" />
          </a>
        </Button>
      </div>
    </div>
  );
}
